/**
 * PropertyChip — FreshVibe Way
 * A property value rendered as a chip with inline pop-up editing.
 * Tapping the chip opens a small editing surface right there.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Text, Select, TextInput, ActionIcon, Stack } from '@mantine/core';
import { PropertyValue, PropertyDefinition } from '../../../types/models/Property';
import { Chip } from './Chip';

export interface PropertyChipProps {
  /** The property definition */
  property: PropertyDefinition;
  /** Current value */
  value: PropertyValue | undefined;
  /** Called when user saves a new value */
  onChange?: (value: PropertyValue) => void;
  /** Called when user wants to open the full page */
  onOpenPage?: () => void;
  /** Read-only (no editing) */
  readOnly?: boolean;
  /** Compact mode for smaller chips */
  compact?: boolean;
  className?: string;
}

/** Map Notion-style color names to CSS hex */
const STATUS_COLORS: Record<string, string> = {
  gray: '#9b9a97', blue: '#3370d4', green: '#1a9a3c',
  yellow: '#d4a503', orange: '#d9730d', red: '#d4451a',
  purple: '#9065b0', pink: '#c14f8d', brown: '#8a5e43',
};

/** Status options that every database gets */
export const DEFAULT_STATUS_OPTIONS = [
  { id: 'status_todo', name: 'To Do', color: '#9b9a97' },
  { id: 'status_in_progress', name: 'In Progress', color: '#3370d4' },
  { id: 'status_done', name: 'Done', color: '#1a9a3c' },
];

/** Parse a property value into a display label + optional color */
function parsePropertyDisplay(property: PropertyDefinition, value: PropertyValue | undefined): { label: string; color?: string } {
  if (!value) return { label: `No ${property.name}` };

  switch (property.type) {
    case 'status':
    case 'select': {
      // Value may be an object { id, name, color } or just a string
      if (typeof value === 'object' && value !== null && 'name' in (value as object)) {
        const obj = value as { name: string; color?: string; id?: string };
        const hexColor = obj.color || (obj.id ? STATUS_COLORS[obj.id.replace('status_', '').replace('_', '')] : undefined);
        return { label: obj.name, color: hexColor };
      }
      return { label: String(value), color: undefined };
    }
    case 'multi_select':
    case 'tag': {
      if (Array.isArray(value)) {
        return { label: `${value.length} tags`, color: '#9065b0' };
      }
      return { label: String(value), color: undefined };
    }
    case 'number':
      return { label: String(value), color: undefined };
    case 'date': {
      const d = new Date(String(value));
      if (!isNaN(d.getTime())) {
        return { label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) };
      }
      return { label: String(value), color: undefined };
    }
    case 'url':
    case 'email':
      return { label: String(value), color: '#3370d4' };
    case 'checkbox':
      return { label: value ? '✓ Done' : '☐ To do', color: value ? '#1a9a3c' : '#9b9a97' };
    default:
      return { label: String(value).slice(0, 20), color: undefined };
  }
}

export const PropertyChip: React.FC<PropertyChipProps> = ({
  property,
  value,
  onChange,
  onOpenPage,
  readOnly = false,
  compact = false,
  className = '',
}) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [editValue, setEditValue] = useState<PropertyValue | undefined>(value);
  const chipRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const { label, color } = parsePropertyDisplay(property, value);

  // Close on outside click
  useEffect(() => {
    if (!popupOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        chipRef.current && !chipRef.current.contains(e.target as Node) &&
        popupRef.current && !popupRef.current.contains(e.target as Node)
      ) {
        setPopupOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popupOpen]);

  const handleChipClick = useCallback(() => {
    if (readOnly) { onOpenPage?.(); return; }
    setEditValue(value);
    setPopupOpen(o => !o);
  }, [readOnly, onOpenPage, value]);

  const handleSave = useCallback(() => {
    onChange?.(editValue!);
    setPopupOpen(false);
  }, [editValue, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setPopupOpen(false);
  }, [handleSave]);

  // Render the appropriate input for the property type
  const renderInput = () => {
    if (!property) return null;
    switch (property.type) {
      case 'status':
      case 'select': {
        const options = (property.options || DEFAULT_STATUS_OPTIONS).map(o => ({
          value: o.id,
          label: o.name,
        }));
        return (
          <Select
            value={typeof editValue === 'object' && editValue !== null && 'id' in (editValue as object) ? (editValue as {id: string}).id : ''}
            onChange={v => {
              const opt = (property.options || DEFAULT_STATUS_OPTIONS).find(o => o.id === v);
              setEditValue({ id: v, name: opt?.name || v, color: opt?.color || '#9b9a97' });
            }}
            data={options}
            size="sm"
            autoFocus
            onKeyDown={handleKeyDown}
          />
        );
      }
      case 'text':
      case 'title': {
        return (
          <TextInput
            value={typeof editValue === 'string' ? editValue : ''}
            onChange={e => setEditValue(e.target.value as PropertyValue)}
            size="sm"
            autoFocus
            onKeyDown={handleKeyDown}
          />
        );
      }
      case 'number': {
        return (
          <TextInput
            type="number"
            value={typeof editValue === 'number' ? String(editValue) : ''}
            onChange={e => setEditValue(parseFloat(e.target.value) as PropertyValue)}
            size="sm"
            autoFocus
            onKeyDown={handleKeyDown}
          />
        );
      }
      case 'date': {
        const dateVal = typeof editValue === 'string' ? editValue : '';
        return (
          <TextInput
            type="date"
            value={dateVal}
            onChange={e => setEditValue(e.target.value as PropertyValue)}
            size="sm"
            autoFocus
            onKeyDown={handleKeyDown}
          />
        );
      }
      default:
        return (
          <TextInput
            value={String(editValue ?? '')}
            onChange={e => setEditValue(e.target.value as PropertyValue)}
            size="sm"
            autoFocus
            onKeyDown={handleKeyDown}
          />
        );
    }
  };

  return (
    <Box style={{ position: 'relative', display: 'inline-block' }} className={className}>
      {/* The chip itself */}
      <Box ref={chipRef} onClick={handleChipClick} style={{ display: 'inline-block', cursor: readOnly ? 'default' : 'pointer' }}>
        <Chip
          label={label}
          color={color}
          variant={property.type === 'status' || property.type === 'select' ? 'status' : 'default'}
        />
      </Box>

      {/* Pop-up editor */}
      {popupOpen && (
        <Box
          ref={popupRef}
          className="fc-prop-popup"
          style={{
            top: 'calc(100% + 6px)',
            left: 0,
            animation: 'fc-scale-in var(--fc-motion-base) forwards',
          }}
          onKeyDown={handleKeyDown}
        >
          {/* Property name header */}
          <Text size="xs" fw={600} c="dimmed" mb={8} tt="uppercase" style={{ letterSpacing: '0.04em' }}>
            {property.name}
          </Text>

          {/* Input */}
          {renderInput()}

          {/* Action buttons */}
          <Box style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            <Box
              component="button"
              onClick={handleSave}
              style={{
                flex: 1,
                padding: '6px 12px',
                borderRadius: 'var(--fc-radius-md)',
                border: 'none',
                background: 'var(--fc-gray-700)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background var(--fc-motion-fast)',
              }}
            >
              Save
            </Box>
            <Box
              component="button"
              onClick={() => setPopupOpen(false)}
              style={{
                padding: '6px 12px',
                borderRadius: 'var(--fc-radius-md)',
                border: '1px solid var(--fc-border-primary)',
                background: 'transparent',
                color: 'var(--fc-text-secondary)',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background var(--fc-motion-fast)',
              }}
            >
              Cancel
            </Box>
          </Box>

          {/* Open full page link */}
          {onOpenPage && (
            <Box
              component="button"
              onClick={() => { setPopupOpen(false); onOpenPage(); }}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 8,
                padding: '4px 0',
                border: 'none',
                background: 'transparent',
                color: 'var(--fc-accent-primary)',
                fontSize: 12,
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'opacity var(--fc-motion-fast)',
              }}
            >
              Open full page →
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};